import React, { useState, useEffect, useCallback } from "react";
import {
  Row,
  Col,
  Card,
  Space,
  Button,
  Statistic,
  Select,
  Table,
  Tag,
  Modal,
  Form,
  Tooltip,
  Popconfirm,
} from "antd";
import {
  PlusOutlined,
  SyncOutlined,
  UserOutlined,
  SwapOutlined,
  EditOutlined,
  DeleteOutlined,
  TeamOutlined,
  BookOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import useClassTeachers from "../../../hooks/academics/useClassTeachers";
import useAcademicYears from "../../../hooks/common/useAcademicYears";
import useTerms from "../../../hooks/common/useTerms";
import Utilities from "../../../dependencies/custom/react-utilities";
import domainSettings from "../../../dependencies/custom/settings";

const ClassTeacherAssignment = () => {
  const navigate = useNavigate();

  const {
    assignments,
    loading,
    fetchAssignments,
    createAssignment,
    updateAssignment,
    deleteAssignment,
    swapTeachers,
  } = useClassTeachers();

  const { academicYears, fetchAcademicYears } = useAcademicYears();
  const { terms, fetchTerms } = useTerms();

  const [staff, setStaff] = useState([]);
  const [classes, setClasses] = useState([]);
  const [streams, setStreams] = useState([]);

  // Filters
  const [filterAcadyrID, setFilterAcadyrID] = useState(null);
  const [filterTermID, setFilterTermID] = useState(null);
  const [filterClassID, setFilterClassID] = useState(null);
  const [filterStreamID, setFilterStreamID] = useState(null);

  // Assignment modal
  const [modalVisible, setModalVisible] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();

  // Swap modal
  const [swapModalVisible, setSwapModalVisible] = useState(false);
  const [swappingRecord, setSwappingRecord] = useState(null);
  const [swapTargetID, setSwapTargetID] = useState(null);
  const [swapping, setSwapping] = useState(false);

  const fetchBootstrapData = useCallback(async () => {
    const [staffRes, classRes, streamRes] = await Promise.all([
      Utilities.fetchWithAuth(
        "GET",
        domainSettings.urls.auth.listStaff,
        null,
        navigate,
      ),
      Utilities.fetchWithAuth(
        "GET",
        domainSettings.urls.bootstrap.classes,
        null,
        navigate,
      ),
      Utilities.fetchWithAuth(
        "GET",
        domainSettings.urls.bootstrap.stream,
        null,
        navigate,
      ),
    ]);
    if (staffRes?.ok)
      setStaff(
        Array.isArray(staffRes.data)
          ? staffRes.data
          : staffRes.data?.results || [],
      );
    if (classRes?.ok)
      setClasses(
        Array.isArray(classRes.data)
          ? classRes.data
          : classRes.data?.results || [],
      );
    if (streamRes?.ok)
      setStreams(
        Array.isArray(streamRes.data)
          ? streamRes.data
          : streamRes.data?.results || [],
      );
  }, [navigate]);

  useEffect(() => {
    fetchAcademicYears();
    fetchTerms();
    fetchBootstrapData();
  }, [fetchAcademicYears, fetchTerms, fetchBootstrapData]);

  useEffect(() => {
    const params = {};
    if (filterAcadyrID) params.academic_year = filterAcadyrID;
    if (filterTermID) params.term = filterTermID;
    if (filterClassID) params.school_class = filterClassID;
    if (filterStreamID) params.stream = filterStreamID;
    fetchAssignments(params);
  }, [
    filterAcadyrID,
    filterTermID,
    filterClassID,
    filterStreamID,
    fetchAssignments,
  ]);

  const refresh = () => {
    const params = {};
    if (filterAcadyrID) params.academic_year = filterAcadyrID;
    if (filterTermID) params.term = filterTermID;
    if (filterClassID) params.school_class = filterClassID;
    if (filterStreamID) params.stream = filterStreamID;
    fetchAssignments(params);
  };

  const handleOpenCreate = () => {
    setEditingRecord(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleOpenEdit = (record) => {
    setEditingRecord(record);
    form.setFieldsValue({
      userID: record.userID,
      classID: record.classID,
      streamID: record.streamID,
      acadyrID: record.acadyrID,
      termID: record.termID,
    });
    setModalVisible(true);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      let result;
      if (editingRecord) {
        result = await updateAssignment(editingRecord.id, values);
      } else {
        result = await createAssignment(values);
      }
      setSaving(false);
      if (result) {
        setModalVisible(false);
        form.resetFields();
        setEditingRecord(null);
        refresh();
      }
    } catch {
      setSaving(false);
    }
  };

  const handleDelete = async (record) => {
    const ok = await deleteAssignment(record.id);
    if (ok) refresh();
  };

  const handleOpenSwap = (record) => {
    setSwappingRecord(record);
    setSwapTargetID(null);
    setSwapModalVisible(true);
  };

  const handleSwap = async () => {
    if (!swapTargetID) return;
    setSwapping(true);
    const ok = await swapTeachers(swappingRecord.id, swapTargetID);
    setSwapping(false);
    if (ok) {
      setSwapModalVisible(false);
      setSwappingRecord(null);
      setSwapTargetID(null);
      refresh();
    }
  };

  // Stats
  const uniqueClasses = new Set(assignments.map((a) => a.classID)).size;
  const uniqueTeachers = new Set(assignments.map((a) => a.userID)).size;

  const columns = [
    {
      title: "Teacher",
      dataIndex: "teacherName",
      key: "teacherName",
      sorter: (a, b) =>
        (a.teacherName || "").localeCompare(b.teacherName || ""),
    },
    {
      title: "Class",
      dataIndex: "className",
      key: "className",
      sorter: (a, b) => (a.className || "").localeCompare(b.className || ""),
    },
    {
      title: "Stream",
      dataIndex: "streamName",
      key: "streamName",
      render: (text) => (text ? <Tag color="blue">{text}</Tag> : "-"),
    },
    {
      title: "Term",
      dataIndex: "term",
      key: "term",
      render: (text) => text || "-",
    },
    {
      title: "Academic Year",
      dataIndex: "academicYear",
      key: "academicYear",
    },
    {
      title: "Assigned On",
      dataIndex: "assignedAt",
      key: "assignedAt",
      sorter: (a, b) => new Date(a.assignedAt) - new Date(b.assignedAt),
      render: (date) => (date ? dayjs(date).format("DD MMM YYYY") : "-"),
    },
    {
      title: "Actions",
      key: "actions",
      align: "center",
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="Edit">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => handleOpenEdit(record)}
            />
          </Tooltip>
          <Tooltip title="Swap Teacher">
            <Button
              type="text"
              style={{ color: "#722ed1" }}
              icon={<SwapOutlined />}
              onClick={() => handleOpenSwap(record)}
            />
          </Tooltip>
          <Tooltip title="Remove">
            <Popconfirm
              title="Remove this class teacher assignment?"
              description="This cannot be undone."
              onConfirm={() => handleDelete(record)}
              okText="Remove"
              okButtonProps={{ danger: true }}
              cancelText="Cancel"
            >
              <Button type="text" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          </Tooltip>
        </Space>
      ),
    },
  ];

  const swapOptions = assignments.filter((a) => a.id !== swappingRecord?.id);

  return (
    <div style={{ padding: 24 }}>
      {/* Stats */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="Total Assignments"
              value={assignments.length}
              prefix={<BookOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="Classes Covered"
              value={uniqueClasses}
              prefix={<TeamOutlined />}
              valueStyle={{ color: "#1677ff" }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="Teachers Assigned"
              value={uniqueTeachers}
              prefix={<UserOutlined />}
              valueStyle={{ color: "#52c41a" }}
            />
          </Card>
        </Col>
      </Row>

      {/* Main Card */}
      <Card
        title="Class Teacher Assignments"
        extra={
          <Space>
            <Button icon={<SyncOutlined />} onClick={refresh} loading={loading}>
              Refresh
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleOpenCreate}
            >
              Assign Teacher
            </Button>
          </Space>
        }
      >
        {/* Filters */}
        <Row gutter={12} style={{ marginBottom: 16 }}>
          <Col xs={24} sm={12} md={6}>
            <Select
              placeholder="Academic Year"
              allowClear
              style={{ width: "100%" }}
              value={filterAcadyrID}
              onChange={setFilterAcadyrID}
              showSearch
              optionFilterProp="label"
            >
              {academicYears.map((y) => (
                <Select.Option key={y.id} value={y.id} label={y.acadyr}>
                  {y.acadyr}
                </Select.Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} sm={12} md={5}>
            <Select
              placeholder="Term"
              allowClear
              style={{ width: "100%" }}
              value={filterTermID}
              onChange={setFilterTermID}
              showSearch
              optionFilterProp="label"
            >
              {terms.map((t) => (
                <Select.Option key={t.id} value={t.id} label={t.name}>
                  {t.name}
                </Select.Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Select
              placeholder="Class"
              allowClear
              style={{ width: "100%" }}
              value={filterClassID}
              onChange={setFilterClassID}
              showSearch
              optionFilterProp="label"
            >
              {classes.map((c) => (
                <Select.Option key={c.id} value={c.id} label={c.name}>
                  {c.name}
                </Select.Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} sm={12} md={5}>
            <Select
              placeholder="Stream"
              allowClear
              style={{ width: "100%" }}
              value={filterStreamID}
              onChange={setFilterStreamID}
              showSearch
              optionFilterProp="label"
            >
              {streams.map((s) => (
                <Select.Option key={s.id} value={s.id} label={s.name}>
                  {s.name}
                </Select.Option>
              ))}
            </Select>
          </Col>
        </Row>

        <Table
          rowKey="id"
          columns={columns}
          dataSource={assignments}
          loading={loading}
          pagination={{
            pageSize: 20,
            showSizeChanger: true,
            pageSizeOptions: ["10", "20", "50"],
          }}
          scroll={{ x: 900 }}
          size="small"
        />
      </Card>

      {/* Assign / Edit Modal */}
      <Modal
        title={editingRecord ? "Edit Assignment" : "Assign Class Teacher"}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
          setEditingRecord(null);
        }}
        onOk={handleSubmit}
        confirmLoading={saving}
        okText={editingRecord ? "Save Changes" : "Assign"}
        destroyOnClose
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item
            name="userID"
            label="Teacher (Staff)"
            rules={[{ required: true, message: "Please select a teacher" }]}
          >
            <Select
              placeholder="Select teacher"
              showSearch
              optionFilterProp="label"
              allowClear
            >
              {staff.map((s) => (
                <Select.Option
                  key={s.userID}
                  value={s.userID}
                  label={s.fullName}
                >
                  {s.fullName}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="classID"
            label="Class"
            rules={[{ required: true, message: "Please select a class" }]}
          >
            <Select
              placeholder="Select class"
              showSearch
              optionFilterProp="label"
              allowClear
            >
              {classes.map((c) => (
                <Select.Option key={c.id} value={c.id} label={c.name}>
                  {c.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="streamID"
            label="Stream"
            rules={[{ required: true, message: "Please select a stream" }]}
          >
            <Select
              placeholder="Select stream"
              showSearch
              optionFilterProp="label"
              allowClear
            >
              {streams.map((s) => (
                <Select.Option key={s.id} value={s.id} label={s.name}>
                  {s.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="acadyrID"
            label="Academic Year"
            rules={[
              { required: true, message: "Please select an academic year" },
            ]}
          >
            <Select
              placeholder="Select academic year"
              showSearch
              optionFilterProp="label"
              allowClear
            >
              {academicYears.map((y) => (
                <Select.Option key={y.id} value={y.id} label={y.acadyr}>
                  {y.acadyr}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="termID"
            label="Term"
            rules={[{ required: true, message: "Please select a term" }]}
          >
            <Select
              placeholder="Select term"
              showSearch
              optionFilterProp="label"
              allowClear
            >
              {terms.map((t) => (
                <Select.Option key={t.id} value={t.id} label={t.name}>
                  {t.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      </Modal>

      {/* Swap Teachers Modal */}
      <Modal
        title={
          swappingRecord
            ? `Swap Teacher — ${swappingRecord.className} / ${swappingRecord.streamName}`
            : "Swap Teacher"
        }
        open={swapModalVisible}
        onCancel={() => {
          setSwapModalVisible(false);
          setSwappingRecord(null);
          setSwapTargetID(null);
        }}
        onOk={handleSwap}
        confirmLoading={swapping}
        okText="Confirm Swap"
        okButtonProps={{ disabled: !swapTargetID }}
        destroyOnClose
      >
        <p style={{ marginBottom: 8 }}>
          Current teacher: <strong>{swappingRecord?.teacherName}</strong>
        </p>
        <p style={{ marginBottom: 8 }}>
          Select another assignment to swap teachers with:
        </p>
        <Select
          style={{ width: "100%" }}
          placeholder="Select assignment to swap with"
          showSearch
          optionFilterProp="label"
          value={swapTargetID}
          onChange={setSwapTargetID}
          allowClear
        >
          {swapOptions.map((a) => (
            <Select.Option
              key={a.id}
              value={a.id}
              label={`${a.teacherName} – ${a.className} (${a.streamName})`}
            >
              <strong>{a.teacherName}</strong>
              <span style={{ color: "#888", marginLeft: 6 }}>
                → {a.className} / {a.streamName}
              </span>
            </Select.Option>
          ))}
        </Select>
      </Modal>
    </div>
  );
};

export default ClassTeacherAssignment;
